import { type QueryClient, QueryObserver } from 'react-query'

import type { ChartsV2State } from 'features/charts-v2/types/filter-hash/v2'
import type { StoredPanel } from 'features/charts-v2/types/stored-panel'
import type { FilterOption } from 'features/filters/types'
import { projectFilterListKey } from 'src/hooks/use-project-filter-list'

/** A hash body as stored: its panels in either form. */
export type StoredChartsHashBody = {
  v2: Omit<ChartsV2State['v2'], 'panels'> & { panels: StoredPanel[] }
}

// The project root fetches the hub's options; a stored body waits for that
// hub's copy rather than reading names against another hub's or none.
const FILTER_LIST_WAIT_MS = 15_000

export const whenHubFilterList = (
  queryClient: QueryClient,
  projectId: string,
): Promise<FilterOption[] | null> => {
  const key = projectFilterListKey(projectId)
  const cached = queryClient.getQueryData<FilterOption[]>(key)
  if (cached) return Promise.resolve(cached)
  // The project root never refetches a failed list, so waiting on it is futile.
  if (queryClient.getQueryState(key)?.status === 'error') {
    return Promise.resolve(null)
  }
  return new Promise((resolve) => {
    const observer = new QueryObserver<FilterOption[]>(queryClient, {
      queryKey: key,
      enabled: false,
    })
    const finish = (options: FilterOption[] | null): void => {
      clearTimeout(timer)
      unsubscribe()
      resolve(options)
    }
    const timer = setTimeout(() => finish(null), FILTER_LIST_WAIT_MS)
    const unsubscribe = observer.subscribe((result) => {
      if (result.data) finish(result.data)
      else if (result.isError) finish(null)
    })
  })
}
